/**
 * FTR5: chase + cockpit cameras. The ship sits at render origin (floating
 * origin), so cameras are positioned relative to (0,0,0) and oriented by the
 * ship's quaternion. Cockpit = at the ship; chase = behind + above.
 */
import * as THREE from 'three';
import type { Quat } from '@blackmarble/sim-core';
import { createCameraFeelTuning, type CameraFeelTuning } from '../feelTuning.js';

export type CameraMode = 'cockpit' | 'chase';

/** M3 FLIGHT-FEEL A6: optional tiny camera shake offset (deterministic, client). */
export interface CameraShake {
  pos: THREE.Vector3; // km, ship-relative
  pitch: number; // rad
  yaw: number; // rad
  roll: number; // rad
}

const COCKPIT_OFFSET = new THREE.Vector3(0, 0.5, 0);
const CHASE_OFFSET = new THREE.Vector3(0, 4, 18);

// M4 CONTROL-FEEL: if the ship orientation jumps further than this in one frame
// (teleport, drop, debug hook), snap instead of slow-swinging through the lag.
const SNAP_ANGLE_RAD = Math.PI / 3; // 60°

export class CameraRig {
  mode: CameraMode = 'chase';
  readonly tuning: CameraFeelTuning = createCameraFeelTuning();
  // M4 CONTROL-FEEL: aim stays tight so the reticle follows the ship's nose,
  // while the frame quaternion lags to keep chase/cockpit offsets from orbiting
  // hard around nearby scenery during a quick turn.
  private readonly aimQuat = new THREE.Quaternion();
  private readonly _frameQuat = new THREE.Quaternion();
  private readonly cockpitDeltaQuat = new THREE.Quaternion();

  /** Public getter for the lagged frame quaternion (cockpit parallax). */
  get frameQuat(): Readonly<THREE.Quaternion> { return this._frameQuat; }
  /** Relative cockpit sway: near-identity delta from tight aim to lagged frame. */
  get cockpitFrameDelta(): Readonly<THREE.Quaternion> {
    return this.cockpitDeltaQuat.copy(this.aimQuat).invert().multiply(this._frameQuat);
  }
  private smoothInit = false;
  private readonly shakeQuat = new THREE.Quaternion();
  private readonly shakeEuler = new THREE.Euler();
  // AAA-03: verify-the-name rail-assist beat. Eases the camera to LOOK at the
  // flagged carved name, then holds, until cancelled (player takes the stick).
  // Client-only presentation — never feeds sim state, so it cannot affect replay.
  private beatT = -1; // <0 = inactive
  private beatDur = 0;
  private readonly beatTarget = new THREE.Quaternion();

  constructor(private readonly camera: THREE.PerspectiveCamera) {}

  get tauChase(): number {
    return this.tuning.tauChase;
  }

  set tauChase(value: number) {
    this.tuning.tauChase = value;
  }

  get tauCockpit(): number {
    return this.tuning.tauCockpit;
  }

  set tauCockpit(value: number) {
    this.tuning.tauCockpit = value;
  }

  get tauFrameChase(): number {
    return this.tuning.tauFrameChase;
  }

  set tauFrameChase(value: number) {
    this.tuning.tauFrameChase = value;
  }

  get tauFrameCockpit(): number {
    return this.tuning.tauFrameCockpit;
  }

  set tauFrameCockpit(value: number) {
    this.tuning.tauFrameCockpit = value;
  }

  toggle(): void {
    this.mode = this.mode === 'chase' ? 'cockpit' : 'chase';
    this.snap(); // C-key swap must not swing through the rotational lag
  }

  /** M4: drop the rotational lag — next update copies the ship quat exactly.
   * Call after teleports / supercruise drops / anything that snaps the hull. */
  snap(): void {
    this.smoothInit = false;
  }

  /** True while the verify beat is easing/holding. */
  get verifying(): boolean {
    return this.beatT >= 0;
  }

  /** Begin the verify beat: ease toward an orientation that looks at `lookDir`
   * (render-space delta from the ship to the signal). */
  enterVerify(lookDir: THREE.Vector3, durationSec: number): void {
    const m = new THREE.Matrix4().lookAt(new THREE.Vector3(), lookDir, new THREE.Vector3(0, 1, 0));
    this.beatTarget.setFromRotationMatrix(m);
    this.beatT = 0;
    this.beatDur = Math.max(0.1, durationSec);
  }

  /** Abort the beat (player took the stick). Orientation returns to the ship. */
  cancelVerify(): void {
    this.beatT = -1;
  }

  /** Advance the beat clock; clamps at its duration (then holds). */
  tick(dt: number): void {
    if (this.beatT >= 0) this.beatT = Math.min(this.beatDur, this.beatT + dt);
  }

  /** smoothstep ease 0..1. */
  private static ease(t: number): number {
    const c = Math.max(0, Math.min(1, t));
    return c * c * (3 - 2 * c);
  }

  private static alpha(dt: number, tau: number): number {
    return tau > 0 ? 1 - Math.exp(-Math.max(0, dt) / tau) : 1;
  }

  private syncQuat(current: THREE.Quaternion, target: THREE.Quaternion, dt: number, tau: number): void {
    if (!this.smoothInit || current.angleTo(target) > SNAP_ANGLE_RAD) {
      current.copy(target);
      return;
    }
    current.slerp(target, CameraRig.alpha(dt, tau));
  }

  /**
   * Re-place the camera each frame from the ship's render orientation through
   * separated aim/frame springs: aim tracks the nose tightly for responsive
   * reticle placement, while the frame offset lags to avoid orbit-like camera
   * translation around nearby objects. Optional shake remains client-only.
   */
  update(shipQuat: Quat, dt: number, shake?: CameraShake): void {
    const target = new THREE.Quaternion(shipQuat.x, shipQuat.y, shipQuat.z, shipQuat.w);
    this.syncQuat(this.aimQuat, target, dt, this.mode === 'cockpit' ? this.tauCockpit : this.tauChase);
    this.syncQuat(
      this._frameQuat,
      target,
      dt,
      this.mode === 'cockpit' ? this.tauFrameCockpit : this.tauFrameChase,
    );
    this.smoothInit = true;
    const q = this.aimQuat.clone();
    const offset = (this.mode === 'cockpit' ? COCKPIT_OFFSET : CHASE_OFFSET).clone();
    offset.applyQuaternion(this._frameQuat);
    if (shake) {
      offset.add(shake.pos);
      this.shakeEuler.set(shake.pitch, shake.yaw, shake.roll, 'XYZ');
      this.shakeQuat.setFromEuler(this.shakeEuler);
      q.multiply(this.shakeQuat);
    }
    if (this.beatT >= 0) q.slerp(this.beatTarget, CameraRig.ease(this.beatT / this.beatDur));
    this.camera.position.copy(offset);
    this.camera.quaternion.copy(q);
  }
}
